import { useEffect, useMemo, useRef } from 'react';
import {
  useGetInvoiceBankAccountsForCompanyLookupQuery,
} from '../Service/bankAccountsLookupApi';
import type { InvoiceBankAccountLookup } from '../Service/bankAccountsLookupMapper';
import { selectPreferredBankAccount } from './selectPreferredBankAccount';

export type BankAccountOption = {
  id: number | null;
  label: string;
  value: string;
  isDefault?: boolean;
  uid: string;
};

const getSelectionContextKey = (companyId?: number, currency?: string): string =>
  `${companyId ?? 'none'}::${currency?.trim().toUpperCase() ?? ''}`;

export const useBankAccountOptions = (
  companyId?: number,
  currency?: string,
  currentValue?: string,
  onAutoSelect?: (value: string) => void
) => {
  const { data: companyAccounts } = useGetInvoiceBankAccountsForCompanyLookupQuery(companyId ?? 0, {
    skip: typeof companyId !== 'number',
  });
  const lastAutoSelectedValueRef = useRef<string | undefined>(undefined);
  const previousSelectionContextRef = useRef<string | undefined>(undefined);

  const accounts = useMemo<InvoiceBankAccountLookup[]>(() => companyAccounts ?? [], [companyAccounts]);

  const options: BankAccountOption[] = useMemo(() => {
    const seen = new Set<string>();
    return accounts
      .map((account, idx: number) => {
        const number = account.iban || account.accountNumber || '';
        const label = [account.name, account.bankName, number, account.currency]
          .filter(Boolean)
          .join(' • ');
        const uid = `${account.id ?? `idx-${idx}`}::${number}`;

        return {
          id: account.id ?? null,
          uid,
          label,
          value: number,
          isDefault: !!account.isDefault,
        };
      })
      .filter((option) => (seen.has(option.uid) ? false : (seen.add(option.uid), true)));
  }, [accounts]);

  const preferredOption = useMemo(() => {
    const preferredAccount = selectPreferredBankAccount(accounts, currency);

    if (!preferredAccount) {
      return undefined;
    }

    const preferredValue = preferredAccount.iban || preferredAccount.accountNumber || '';

    return options.find((option) => option.value === preferredValue);
  }, [accounts, currency, options]);

  useEffect(() => {
    if (!onAutoSelect) {
      return;
    }

    const currentSelectionContext = getSelectionContextKey(companyId, currency);
    const previousSelectionContext = previousSelectionContextRef.current;
    const hasSelectionContextChanged =
      previousSelectionContext !== undefined && previousSelectionContext !== currentSelectionContext;

    previousSelectionContextRef.current = currentSelectionContext;

    const nextValue = preferredOption?.value ?? '';

    if (hasSelectionContextChanged) {
      lastAutoSelectedValueRef.current = nextValue || undefined;

      if (nextValue !== currentValue) {
        onAutoSelect(nextValue);
      }

      return;
    }

    const shouldAutoSelect = !currentValue || currentValue === lastAutoSelectedValueRef.current;

    if (!shouldAutoSelect || nextValue === currentValue) {
      return;
    }

    lastAutoSelectedValueRef.current = nextValue || undefined;
    onAutoSelect(nextValue);
  }, [companyId, currency, currentValue, onAutoSelect, preferredOption]);

  return { options };
};
